import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Paper, CircularProgress, useMediaQuery, useTheme, IconButton, Fade } from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

const getMessageText = (message) => {
  const value = message?.texto ?? message?.content ?? message?.message ?? "";
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const TypingIndicator = ({ isUser }) => {
  return (
    <Box
      sx={{
        alignSelf: "stretch",
        width: "100%",
        display: "flex",
        justifyContent: isUser ? "flex-start" : "flex-end",
      }}
    >
      <Paper
        sx={{
          maxWidth: "70px",
          p: 1.5,
          bgcolor: isUser ? "background.paper" : "success.light",
          borderRadius: "10px",
          boxShadow: 1,
          display: "flex",
          gap: 0.5,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "text.secondary",
              animation: "typing 1.4s infinite",
              animationDelay: `${i * 0.2}s`,
              "@keyframes typing": {
                "0%, 60%, 100%": { transform: "translateY(0)", opacity: 0.7 },
                "30%": { transform: "translateY(-10px)", opacity: 1 },
              },
            }}
          />
        ))}
      </Paper>
    </Box>
  );
};


const ChatWindow = ({ selectedContact, endpointPrefix = "sdr", setForceTokenExpired, sx }) => {
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showTypingIndicator, setShowTypingIndicator] = useState(null); // 'user' ou 'agent'
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const previousMessagesCount = useRef(0);
  const shouldStickToBottomRef = useRef(true);
  const isFetchingRef = useRef(false);
  const fetchMessagesRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isAtBottom = () => {
    const el = messagesContainerRef.current;
    if (!el) return true;
    const threshold = 24;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  };

  const fetchMessages = async (isRefresh = false) => {
    if (!selectedContact || isFetchingRef.current) return;
    isFetchingRef.current = true;

    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoadingMessages(true);
    }

    try {
      const token = localStorage.getItem("jwtToken");
      if (!token) return;
      const decodedToken = jwtDecode(token);
      let params = {};

      if (endpointPrefix === "posv" || endpointPrefix === "AV") {
        params.idCliente = decodedToken.id_cliente;
        params.whatsAppId = selectedContact.whatsAppId;
        if (endpointPrefix === "posv") {
          params.idCliente = decodedToken.id_cliente;
          params.WhatsAppId = selectedContact.whatsAppId;
        }
      } else {
        params.idClienteAipim = decodedToken.id_cliente;
        params.whatsAppId = selectedContact.whatsAppId;
      }

      const url = endpointPrefix === "AV"
        ? `${baseURL}/produto/AV/lista-mensagens`
        : `${baseURL}/produto/${endpointPrefix}/lista-mensagens`;

      const response = await axios.get(url, {
        params,
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });

      const newMessages = response.data;

      // Verifica se há novas mensagens
      if (isRefresh && newMessages.length > previousMessagesCount.current) {
        const lastMessage = newMessages[newMessages.length - 1];
        const isUserMessage = lastMessage.role === "user";

        // Mostra indicador de digitação
        setShowTypingIndicator(isUserMessage ? "user" : "agent");

        // Aguarda 2 segundos antes de mostrar as mensagens
        await new Promise(resolve => setTimeout(resolve, 2000));

        setShowTypingIndicator(null);
      }

      shouldStickToBottomRef.current = isAtBottom() || previousMessagesCount.current === 0;
      setMessages(newMessages);
      previousMessagesCount.current = newMessages.length;

    } catch (error) {
      console.error("Erro ao buscar mensagens:", error);

      if (
        error.response?.status === 401 ||
        error.response?.data?.message?.toLowerCase().includes("token")
      ) {
        setForceTokenExpired(true);
      }
    } finally {
      setLoadingMessages(false);
      setRefreshing(false);
      isFetchingRef.current = false;
    }
  };

  fetchMessagesRef.current = fetchMessages;

  useEffect(() => {
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact, endpointPrefix]);

  useEffect(() => {
    if (!selectedContact) return undefined;
    const intervalId = window.setInterval(() => {
      if (!document.hidden) fetchMessagesRef.current?.(true);
    }, 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, [selectedContact, endpointPrefix]);

  useEffect(() => {
    shouldStickToBottomRef.current = true;
  }, [selectedContact]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (shouldStickToBottomRef.current) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ block: "end" });
      });
    }
  }, [messages, showTypingIndicator]);

  const handleRefresh = () => {
    fetchMessages(true);
  };

  const handleScroll = () => {
    shouldStickToBottomRef.current = isAtBottom();
  };

  if (!selectedContact) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "#0b141a" : "#efeae2",
          color: "text.secondary",
          backgroundImage: (theme) =>
            theme.palette.mode === "dark"
              ? "radial-gradient(circle at 16px 16px, rgba(255,255,255,0.035) 1px, transparent 1px)"
              : "radial-gradient(circle at 16px 16px, rgba(0,0,0,0.035) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Selecione um contato para visualizar a conversa
        </Typography>
      </Box>
    );
  }

  if (loadingMessages) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.paper",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
        p: 0,
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
        isolation: "isolate",
        ...(sx || {}),
      }}
    >
      {(selectedContact?.nome) && !isMobile && (
        <Box
          sx={{
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "#202c33" : "#f0f2f5",
            px: 2,
            py: 1.5,
            borderRadius: 0,
            color: "text.primary",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", fontSize: "1rem" }}
          >
            {selectedContact.nome}
          </Typography>

          <IconButton
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{
              color: "text.secondary",
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
          >
            <RefreshIcon
              sx={{
                animation: refreshing ? "spin 1s linear infinite" : "none",
                "@keyframes spin": {
                  "0%": {
                    transform: "rotate(0deg)",
                  },
                  "100%": {
                    transform: "rotate(360deg)",
                  },
                },
              }}
            />
          </IconButton>
        </Box>
      )}

      <Box
        ref={messagesContainerRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          minWidth: 0,
          maxWidth: "100%",
          overflowX: "hidden",
          border: "none",
          borderColor: "divider",
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#0b141a" : "#efeae2",
          backgroundImage: (theme) =>
            theme.palette.mode === "dark"
              ? "radial-gradient(circle at 16px 16px, rgba(255,255,255,0.035) 1px, transparent 1px)"
              : "radial-gradient(circle at 16px 16px, rgba(0,0,0,0.035) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          p: { xs: 1.5, md: 2 },
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "divider",
            borderRadius: "8px",
          },
        }}
      >
        {messages.map((msg, index) => {
          const messageDate = msg.dataHoraCadastroAtualizado;
          const isUser = msg.role === "user";
          const isAiDirect = msg.role === "aidirect";
          const messageText = getMessageText(msg);

          return (
            <Fade key={index} in={true} timeout={500}>
              <Paper
                sx={{
                  maxWidth: { xs: "88%", md: "72%" },
                  minWidth: { xs: "180px", md: "220px" },
                  p: 1.5,
                  alignSelf: isUser ? "flex-start" : "flex-end",
                  bgcolor: (theme) => {
                    if (theme.palette.mode === "dark") {
                      if (isUser) return "#202c33";
                      return isAiDirect ? "#0b6b60" : "#005c4b";
                    }
                    if (isUser) return "#ffffff";
                    return isAiDirect ? "#cfe8ff" : "#d9fdd3";
                  },
                  color: "text.primary",
                  border: isAiDirect ? "1px solid" : "none",
                  borderColor: isAiDirect ? (theme) => theme.palette.mode === "dark" ? "#4db6ac" : "#1976d2" : "transparent",
                  borderRadius: "10px",
                  fontSize: "0.95rem",
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 1px 2px rgba(0,0,0,0.28)"
                      : "0 1px 2px rgba(15,23,42,0.12)",
                  position: "relative",
                  overflow: "visible",
                  boxSizing: "border-box",
                  display: "block",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: "bold",
                    color: isUser ? "warning.main" : isAiDirect ? "info.dark" : "primary.main",
                    fontSize: "0.75rem",
                    mb: 0.5,
                    display: "block",
                  }}
                >
                  {isUser ? selectedContact.nome : isAiDirect ? "Humano via aiDirect" : "Agente"}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mb: "1.5rem",
                    maxWidth: "100%",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    whiteSpace: "pre-wrap",
                    direction: "ltr",
                    unicodeBidi: "isolate",
                    display: "block",
                    lineHeight: 1.45,
                  }}
                >
                  {messageText}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    bottom: 4,
                    right: 6,
                    fontSize: "0.7rem",
                    color: "text.secondary",
                  }}
                >
                  {new Date(messageDate).toLocaleString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              </Paper>
            </Fade>
          );
        })}

        {showTypingIndicator && (
          <Fade in={true} timeout={300}>
            <Box>
              <TypingIndicator isUser={showTypingIndicator === "user"} />
            </Box>
          </Fade>
        )}
        <Box ref={messagesEndRef} />
      </Box>
    </Box>
  );
};

export default ChatWindow;
